import { NavLink } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { NAV_ITEMS } from './navigation'
import styles from './Sidebar.module.css'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
  /** Count shown against Trip requests — the queue that needs attention. */
  pendingRequests?: number
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  pendingRequests = 0,
}: SidebarProps) {
  return (
    <>
      {mobileOpen ? <div className={styles.scrim} onClick={onCloseMobile} aria-hidden="true" /> : null}

      <aside
        className={cn(styles.sidebar, collapsed && styles.collapsed, mobileOpen && styles.mobileOpen)}
        data-print="hide"
      >
        <div className={styles.brand}>
          <img src="/brand/mark.svg" alt="" className={styles.mark} />
          <span className={styles.brandText}>
            <span className={styles.brandName}>{env.appName}</span>
            <span className={styles.brandRole}>Operations</span>
          </span>
          <button type="button" className={styles.closeMobile} onClick={onCloseMobile} aria-label="Close navigation">
            <X aria-hidden="true" />
          </button>
        </div>

        <nav className={styles.nav} aria-label="Main">
          <p className={styles.navLabel}>Operations</p>
          <ul role="list" className={styles.navList}>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              const badge = item.to === '/trip-requests' ? pendingRequests : 0

              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.exact}
                    onClick={onCloseMobile}
                    className={({ isActive }) => cn(styles.navItem, isActive && styles.navItemActive)}
                    // The label has to survive the collapsed state, where the
                    // text is hidden and only the icon remains.
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className={styles.navIcon} aria-hidden="true" />
                    <span className={styles.navText}>
                      <span className={styles.navTitle}>{item.label}</span>
                      <span className={styles.navDescription}>{item.description}</span>
                    </span>
                    {badge > 0 ? (
                      <span className={styles.navBadge} aria-label={`${badge} awaiting approval`}>
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        <button type="button" className={styles.collapseToggle} onClick={onToggleCollapsed}>
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
          <span className={styles.navText}>Collapse</span>
        </button>
      </aside>
    </>
  )
}
